import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { Box, Grid, IconButton, Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useStyles from "./styles";
import { Contact } from "Models/contact";
import { Button } from "Components/Form";
import { Timezone } from "Models/timezone";
import { Color } from "Components/Form/Button";
import { useAppState } from "Context/StateContext";
import { Close as CloseIcon } from "@mui/icons-material";
import { ContactCard } from "../ContactCard";
import { createContactValidationSchema } from "../../../schemas/contactSchema";

interface Props {
  timezones?: Timezone[];
}

type ContactModalAction = 'save' | 'update' | 'cancel';

interface ContactModalResult {
  agent: any;
  action: ContactModalAction;
  contact?: Contact;
}

export interface ContactModalRef {
  open: (agent: any, contact?: Contact) => Promise<ContactModalResult>;
  close: () => void;
}

const ContactCreateModal = forwardRef<ContactModalRef, Props>((props, ref) => {
  const { timezones } = props;
  const { currentUser } = useAppState();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [agent, setAgent] = useState<any>(null);
  const [contact, setContact] = useState<Contact | undefined>();
  const [promiseResolve, setPromiseResolve] = useState<((result: ContactModalResult) => void) | null>(null);
  
  const [contactData, setContactData] = useState<Contact>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const classes = useStyles();
  const { t: translate } = useTranslation();

  useImperativeHandle(ref, () => ({
    open: (agentData: any, contactToEdit?: Contact): Promise<ContactModalResult> => {
      return new Promise((resolve) => {
        setAgent(agentData);
        setContact(contactToEdit);
        setPromiseResolve(() => resolve);
        setIsOpen(true);
      });
    },
    close: () => {
      handleModalClose('cancel');
    }
  }));

  const isPrimaryContact = useMemo(() => {
    if (!agent?.contacts || agent.contacts.length === 0) return true;

    if (!contact) return false;

    if (contact.isPrimary) return true;

    const firstContact = agent.contacts[0];
    return (
      firstContact.firstName === contact.firstName &&
      firstContact.lastName === contact.lastName &&
      firstContact.email === contact.email
    );
  }, [agent?.contacts, contact]);

  useEffect(() => {
    if (isSubmitted) handleValidate();
  }, [isSubmitted, contactData?.firstName, contactData?.lastName, contactData?.email, contactData?.mobilePhone, contactData?.timezoneId]);

  useEffect(() => {
    if (!isOpen) return;
    
    if (contact) {
      console.log("Editing contact", contact);
      setContactData({
        ...contact,
        firstName: contact?.firstName,
        lastName: contact?.lastName,
        email: contact?.email,
        mobilePhone: contact?.mobilePhone,
        phone: contact?.phone,
        contactType: contact?.contactType ?? (isPrimaryContact ? 'Primary' : 'Secondary'),
        timezoneId: contact?.timezoneId,
        timezone: timezones?.find((timezone) => timezone.id === contact?.timezoneId),
      });
    } else {
      console.log("Creating new contact");
      setContactData({
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: '',
        phone: '',
        contactType: (!agent?.contacts || agent.contacts.length === 0) ? 'Primary' : 'Secondary',
        timezoneId: undefined,
        timezone: undefined,
      } as Contact);
    }
  }, [contact, isOpen, isPrimaryContact, agent?.contacts, timezones]);

  useEffect(() => {
    if (isOpen === false) {
      setContactData({} as Contact);
      setIsSubmitted(false);
      setErrors({});
    }
  }, [isOpen]);

  const handleValidate = () => {
    try {
      const schema = createContactValidationSchema({ translate });

      schema.validateSync(contactData, { abortEarly: false });

      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            validationErrors[err.path] = err.message;
          }
        });
        setErrors(validationErrors);
      }
      return false;
    }
  };

  const handleModalClose = (action: ContactModalAction, updatedContact?: Contact) => {
    if (promiseResolve) {
      const result: ContactModalResult = {
        agent,
        action,
        ...(updatedContact && { contact: updatedContact })
      };
      
      promiseResolve(result);
    }

    // Reset state
    setIsOpen(false);
    setAgent(null);
    setContact(undefined);
    setPromiseResolve(null);
    setContactData({} as Contact);
    setIsSubmitted(false);
    setErrors({});
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    if (handleValidate()) {
      const updatedContact: Contact = {
        ...contactData,
        companyId: currentUser?.companyId,
        valid: true,
        status: "Active",
      } as Contact;

      // Preservar o ID se estiver editando
      if (contact?.id) {
        updatedContact.id = contact.id;
        handleModalClose('update', { ...contact, ...updatedContact });
      } else {
        handleModalClose('save', updatedContact);
      }
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setContactData(prev => ({
      ...(prev ?? {} as Contact),
      [field]: value
    }));
  };

  const onModalClose = () => {
    handleModalClose('cancel');
  };

  return (
    <Modal
      open={isOpen}
      onClose={onModalClose}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div>
        <div className={classes.paper}>
          <div className={classes.modalBody}>
            <Grid container>
              <Grid item xs={12} className={classes.header}>
                <div className={classes.locationModalTitle}>
                  <Box>
                    <span data-cy="locationModalTitle" className={classes.titleName}>
                      {contact ? translate("editAContact") : translate("addNewContact")}
                    </span>
                  </Box>
                  <Box>
                    <IconButton
                      className={classes.iconButton}
                      onClick={onModalClose}
                    >
                      <CloseIcon data-cy="locationCloseIcon" fontSize="medium" />
                    </IconButton>
                  </Box>
                </div>
              </Grid>
              
              <Grid item xs={12}>
                <div className={classes.gridContainerContact}>
                  {contactData && <ContactCard
                    contact={contactData}
                    timezones={timezones}
                    viewOnly={false}
                    isModal={true}
                    errors={errors}
                    onFieldChange={handleFieldChange}
                    classes={classes}
                  />}
                </div>
              </Grid>

              <Grid item xs={12} className={classes.locationButtonsContainer}>
                <Button
                  customColor={Color.CANCEL}
                  data-cy="contact-cancel-button"
                  onClick={onModalClose}
                >
                  {translate("cancel")}
                </Button>
                <Button
                  customColor={Color.SUCCESS}
                  data-cy="contact-save-button"
                  onClick={handleSubmit}
                  className={classes.buttonSave}
                >
                  {contact ? translate("update") : translate("save")}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Modal>
  );
});

ContactCreateModal.displayName = 'ContactCreateModal';

export { ContactCreateModal }